const META_PREFIX = '<!-- ss-plaque:v1';
const META_REGEX = /^<!--\s*ss-plaque:v1\b([^>]*)-->\s*/i;
const IMAGE_REGEX = /^\s*\[\[\s*image\s*:\s*(\d+)(.*?)]]\s*$/i;
const LINK_REGEX = /^\s*\[\[\s*link\s*:\s*(.+?)\s*\|\s*(.+?)\s*]]\s*$/i;
const SUBTITLE_REGEX = /^\s*\[\[\s*subtitle\s*:\s*(h[1-3])(?:\s*\|\s*link\s*=\s*(.+?))?\s*]]\s*$/i;
const SECTION_REGEX = /^\s*\[\[\s*section\s*:\s*(.+?)\s*]]\s*$/i;
const AD_REGEX = /^\s*\[\[\s*ad\s*:\s*(\d+)(.*?)]]\s*$/i;
const AD_PARAM_REGEX = /(?:^|\|)\s*([a-z_]+)\s*=\s*([a-z_0-9]+)\s*/gi;
const CAMPAIGN_REGEX = /^\s*\[\[\s*campaign\s*:\s*(\d+)\s*]]\s*$/i;
const DYNAMIC_CAMPAIGN_REGEX = /^\s*\[\[\s*campaigns\s*:\s*([\d,\s]+)\s*]]\s*$/i;
const WIDGET_REGEX = /^\s*\[\[\s*widget\s*:\s*(\d+)\s*]]\s*$/i;

function sanitizeMarkerValue(value) {
  return String(value || '')
    .replace(/\]\]/g, '')
    .replace(/\|/g, '/')
    .trim();
}

function parseImageParams(raw = '') {
  const params = {};
  const parts = String(raw || '')
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean);
  parts.forEach((part) => {
    const [key, ...rest] = part.split('=');
    const normalizedKey = String(key || '').trim().toLowerCase();
    const value = rest.join('=').trim();
    if (!normalizedKey || !value) return;
    params[normalizedKey] = value;
  });
  return params;
}

export function serializePlaqueContent({ blocks = [] } = {}) {
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  const lines = [];
  // Note: auto-translation on landing pages is intentionally not supported.

  for (const block of safeBlocks) {
    if (!block || typeof block !== 'object') continue;
    if (block.type === 'image' && block.assetId) {
      const linkUrl = typeof block.linkUrl === 'string' ? block.linkUrl.trim() : '';
      const alt = typeof block.alt === 'string' ? sanitizeMarkerValue(block.alt) : '';
      const caption = typeof block.caption === 'string' ? sanitizeMarkerValue(block.caption) : '';
      const params = [];
      if (linkUrl) params.push(`link=${sanitizeMarkerValue(linkUrl)}`);
      if (alt) params.push(`alt=${alt}`);
      if (caption) params.push(`caption=${caption}`);
      lines.push(`[[image:${block.assetId}${params.length ? `|${params.join('|')}` : ''}]]`);
      lines.push('');
      continue;
    }
    if (block.type === 'subtitle') {
      const text = typeof block.text === 'string' ? block.text.trim() : '';
      const size = typeof block.size === 'string' ? block.size.trim().toLowerCase() : 'h2';
      const safeSize = ['h1', 'h2', 'h3'].includes(size) ? size : 'h2';
      const linkUrl = typeof block.linkUrl === 'string' ? block.linkUrl.trim() : '';
      lines.push(`[[subtitle:${safeSize}${linkUrl ? `|link=${linkUrl}` : ''}]]`);
      if (text) {
        lines.push(text.replace(/\r?\n/g, ' ').trim());
      }
      lines.push('');
      continue;
    }
    if (block.type === 'section') {
      const title = typeof block.title === 'string' ? block.title.trim() : '';
      if (title) {
        lines.push(`[[section:${title}]]`);
        lines.push('');
      }
      continue;
    }
    if (block.type === 'ad') {
      const adId = Number(block.adId);
      const size = typeof block.size === 'string' ? block.size : 'inline';
      const stick = typeof block.stick === 'string' ? block.stick : '';
      const showText = block.showText !== false;
      if (Number.isFinite(adId) && adId > 0) {
        const params = [];
        if (size && size !== 'inline') params.push(`size=${size}`);
        if (stick) params.push(`stick=${stick}`);
        if (!showText) params.push('show_text=0');
        const suffix = params.length ? `|${params.join('|')}` : '';
        lines.push(`[[ad:${adId}${suffix}]]`);
        lines.push('');
      }
      continue;
    }
    if (block.type === 'widget') {
      const widgetId = Number(block.widgetId);
      if (Number.isFinite(widgetId) && widgetId > 0) {
        lines.push(`[[widget:${widgetId}]]`);
        lines.push('');
      }
      continue;
    }
    if (block.type === 'campaign') {
      const campaignId = Number(block.campaignId);
      if (Number.isFinite(campaignId) && campaignId > 0) {
        lines.push(`[[campaign:${campaignId}]]`);
        lines.push('');
      }
      continue;
    }
    if (block.type === 'campaign_dynamic') {
      const campaignIds = (Array.isArray(block.campaignIds) ? block.campaignIds : [])
        .map((campaignId) => Number(campaignId))
        .filter((campaignId) => Number.isFinite(campaignId) && campaignId > 0);
      if (campaignIds.length) {
        lines.push(`[[campaigns:${campaignIds.join(',')}]]`);
        lines.push('');
      }
      continue;
    }
    if (block.type === 'text') {
      const text = typeof block.text === 'string' ? block.text : '';
      const linkUrl = typeof block.linkUrl === 'string' ? block.linkUrl.trim() : '';
      const linkTitle = typeof block.linkTitle === 'string' ? block.linkTitle.trim() : '';
      if (linkUrl) {
        const safeTitle = linkTitle || linkUrl;
        lines.push(`[[link:${safeTitle}|${linkUrl}]]`);
      }
      if (text.trim()) {
        lines.push(text.trim());
      }
      lines.push('');
    }
  }

  return lines.join('\n').trim() + '\n';
}

export function parsePlaqueContent(raw = '') {
  const source = typeof raw === 'string' ? raw : '';
  let body = source;

  const metaMatch = body.match(META_REGEX);
  if (metaMatch) {
    body = body.slice(metaMatch[0].length);
  }

  const blocks = [];
  const lines = body.split(/\r?\n/);
  let currentText = [];
  let pendingLink = null;

  const flushText = () => {
    const text = currentText.join('\n').trim();
    if (text || pendingLink) {
      blocks.push({
        type: 'text',
        text,
        linkTitle: pendingLink?.title || '',
        linkUrl: pendingLink?.url || '',
      });
    }
    currentText = [];
    pendingLink = null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!String(line || '').trim()) {
      flushText();
      continue;
    }
    const imageMatch = line.match(IMAGE_REGEX);
    if (imageMatch) {
      flushText();
      const imageParams = parseImageParams(imageMatch[2] || '');
      blocks.push({
        type: 'image',
        assetId: Number(imageMatch[1]),
        linkUrl: imageParams.link || '',
        alt: imageParams.alt || '',
        caption: imageParams.caption || '',
      });
      continue;
    }

    const subtitleMatch = line.match(SUBTITLE_REGEX);
    if (subtitleMatch) {
      flushText();
      const nextLine = i + 1 < lines.length ? lines[i + 1] : '';
      i += 1;
      const subtitleText = String(nextLine || '').trim();
      blocks.push({
        type: 'subtitle',
        text: subtitleText,
        size: subtitleMatch[1],
        linkUrl: subtitleMatch[2] ? subtitleMatch[2].trim() : '',
      });
      continue;
    }

    const sectionMatch = line.match(SECTION_REGEX);
    if (sectionMatch) {
      flushText();
      blocks.push({
        type: 'section',
        title: sectionMatch[1] ? sectionMatch[1].trim() : '',
      });
      continue;
    }
    const adMatch = line.match(AD_REGEX);
    if (adMatch) {
      flushText();
      const paramsRaw = adMatch[2] || '';
      const params = {};
      AD_PARAM_REGEX.lastIndex = 0;
      let match = AD_PARAM_REGEX.exec(paramsRaw);
      while (match) {
        params[match[1]] = match[2];
        match = AD_PARAM_REGEX.exec(paramsRaw);
      }
      const size = params.size || 'inline';
      const stick = params.stick || '';
      const showText = params.show_text !== '0';
      blocks.push({
        type: 'ad',
        adId: Number(adMatch[1]),
        size,
        stick,
        showText,
      });
      continue;
    }
    const widgetMatch = line.match(WIDGET_REGEX);
    if (widgetMatch) {
      flushText();
      blocks.push({
        type: 'widget',
        widgetId: Number(widgetMatch[1]),
      });
      continue;
    }
    const campaignMatch = line.match(CAMPAIGN_REGEX);
    if (campaignMatch) {
      flushText();
      blocks.push({
        type: 'campaign',
        campaignId: Number(campaignMatch[1]),
      });
      continue;
    }
    const dynamicCampaignMatch = line.match(DYNAMIC_CAMPAIGN_REGEX);
    if (dynamicCampaignMatch) {
      flushText();
      blocks.push({
        type: 'campaign_dynamic',
        campaignIds: String(dynamicCampaignMatch[1] || '')
          .split(',')
          .map((campaignId) => Number(String(campaignId || '').trim()))
          .filter((campaignId) => Number.isFinite(campaignId) && campaignId > 0),
      });
      continue;
    }

    const linkMatch = line.match(LINK_REGEX);
    if (linkMatch) {
      flushText();
      pendingLink = { title: linkMatch[1].trim(), url: linkMatch[2].trim() };
      continue;
    }

    currentText.push(line);
  }
  flushText();

  if (!blocks.length) {
    blocks.push({ type: 'text', text: body.trim() });
  }

  return { blocks };
}
